import { createApiErrorFromResponse, readApiErrorDetail } from "@airange/shared/api";
import { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import { REDBLUE_API_BASE_URL } from "./env";
import type { RedblueCurrentUser } from "./redblue-api-contract";

type RedblueAdminInitialState = {
  authStatus?: RedblueAdminAuthStatus;
  currentUser?: RedblueCurrentUser | null;
  [key: string]: unknown;
};

export type RedblueAdminAuthStatus = "authenticated" | "no_session" | "no_access";

export type OidcCallbackCompleteResult = {
  redirectPath: string;
};

export { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";

const REDBLUE_ADMIN_APPLICATION_KEY = "redblue-admin";
const REDBLUE_API_BASE_URL_WITHOUT_TRAILING_SLASH = REDBLUE_API_BASE_URL.replace(/\/+$/, "");

function authEndpoint(path: "/authorize" | "/callback" | "/bootstrap" | "/logout") {
  return `${REDBLUE_API_BASE_URL_WITHOUT_TRAILING_SLASH}/auth/${REDBLUE_ADMIN_APPLICATION_KEY}${path}`;
}

function applicationHeaders() {
  return { "X-AIRange-Application-Key": REDBLUE_ADMIN_APPLICATION_KEY };
}

async function parseJson<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export async function startOidcLogin(
  redirectPath: string,
  prompt: "none" | "login" = "login",
  fetchImpl: typeof fetch = fetch,
  navigate: (url: string) => void = (url) => window.location.assign(url),
) {
  const response = await fetchImpl(authEndpoint("/authorize"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...applicationHeaders() },
    body: JSON.stringify({ return_path: redirectPath, prompt }),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  const result = await parseJson<{ authorization_url: string }>(response);
  navigate(result.authorization_url);
}

export async function completeOidcCallback(
  code: string,
  state: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OidcCallbackCompleteResult> {
  const response = await fetchImpl(authEndpoint("/callback"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...applicationHeaders() },
    body: JSON.stringify({ code, state }),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  const result = await parseJson<{ redirect_path: string }>(response);
  return { redirectPath: result.redirect_path };
}

export async function bootstrapSession(fetchImpl: typeof fetch = fetch) {
  const response = await fetchImpl(authEndpoint("/bootstrap"), {
    method: "POST",
    credentials: "include",
    headers: applicationHeaders(),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  return await parseJson<RedblueCurrentUser | null>(response);
}

export async function logout(fetchImpl: typeof fetch = fetch) {
  const response = await fetchImpl(authEndpoint("/logout"), {
    method: "POST",
    credentials: "include",
    headers: applicationHeaders(),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
}

export function syncRedblueAdminInitialState<T extends RedblueAdminInitialState>(
  state: T | undefined,
  next: {
    currentUser: RedblueCurrentUser;
  },
): T {
  return {
    ...(state ?? {}),
    authStatus: "authenticated",
    currentUser: next.currentUser,
  } as T;
}

export function clearRedblueAdminInitialState<T extends RedblueAdminInitialState>(
  state: T | undefined,
  overrides: {
    authStatus?: RedblueAdminAuthStatus;
    currentUser?: RedblueCurrentUser | null;
  } = {},
): T {
  return {
    ...(state ?? {}),
    authStatus: overrides.authStatus ?? "no_session",
    currentUser: overrides.currentUser ?? null,
  } as T;
}
